import React, { useState, useEffect, useRef } from "react"
import { Container, Row, Card, CardBody, Col, Table, Button, ButtonGroup, Badge } from "reactstrap"
import { withRouter } from "react-router-dom"
import { connect } from "react-redux"
import moment from "moment"
import _ from "lodash"
import Flatpickr1 from "react-flatpickr"
import "flatpickr/dist/themes/material_blue.css"

import * as backend from "../../helpers/backend_helper"
import * as func from "../../common/functions"
import Toaster from "../../components/Common/Toaster"
import { ScoreBadge } from "../../components/Common/reportPrintShared"

const Flatpickr: any = Flatpickr1

// Report Submissions tab (admins): column labels + a coloured submitted/expected cell.
const SUB_LABELS: { [k: string]: string } = {
  dailymonitoring: "Daily Onsite", dailywork: "Daily Work", dailydocument: "Daily Documents",
  monthlyonsite: "Monthly Onsite", monthlydocument: "Monthly Documents", monthlywork: "Monthly Work",
  tasksassigned: "Tasks Assigned",
}
// Display order for the Report Submissions columns (Daily Doc Review before Daily Work).
// "Tasks Assigned" (completed early & on-time) is shown last.
const SUB_ORDER = ["dailymonitoring", "dailydocument", "dailywork", "monthlyonsite", "monthlydocument", "monthlywork", "tasksassigned"]
const orderReports = (keys: string[]) => {
  const set = new Set(keys || [])
  const ordered = SUB_ORDER.filter((k) => set.has(k))
  return ordered.concat((keys || []).filter((k) => !SUB_ORDER.includes(k)))
}
const SubCell = ({ r }: { r: any }) => {
  if (!r || !r.assigned) return <span style={{ color: "#c3ccd6" }}>—</span>
  const map: any = {
    complete: { fg: "#1c8577", bg: "#e5f6f2", bd: "#2ea99b", label: "Submitted" },
    partial: { fg: "#b7791f", bg: "#fdf3e3", bd: "#e0a83c", label: "Partial" },
    none: { fg: "#c0392b", bg: "#fdecec", bd: "#e07a6f", label: "Not submitted" },
  }
  const c = map[r.status] || map.none
  return (
    <span title={`${c.label}: ${r.submitted} of ${r.expected}`}
      style={{ display: "inline-block", padding: "2px 10px", borderRadius: 12, fontWeight: 700, fontSize: 12, color: c.fg, background: c.bg, border: `1px solid ${c.bd}` }}>
      {r.submitted}/{r.expected}
    </span>
  )
}

// A single day cell in the Submission Calendar: Y (submitted), N (missed),
// WO (weekly off / Sunday), or blank (not yet due).
const CalCell = ({ v }: { v: string }) => {
  const map: any = {
    Y: { fg: "#1c8577", bg: "#e5f6f2" },
    N: { fg: "#c0392b", bg: "#fdecec" },
    WO: { fg: "#6b7785", bg: "#eef1f4" },
    H: { fg: "#b7791f", bg: "#fdf3e3" },   // Holiday
    A: { fg: "#5b4b9b", bg: "#efeaf9" },   // Audit
  }
  const c = map[v]
  return (
    <span style={{ display: "block", fontWeight: 700, fontSize: 11, color: c ? c.fg : "#c3ccd6", background: c ? c.bg : "transparent", borderRadius: 3, minWidth: 20, padding: "1px 0" }}>
      {v || "·"}
    </span>
  )
}

const PerformaceScore = (props: any) => {
  const { selectedFactory } = props
  const [range, setRange] = useState<string[]>([
    moment().startOf("month").format("YYYY-MM-DD"),
    moment().format("YYYY-MM-DD"),
  ])
  const [userList, setUserList] = useState<any[]>([])
  const [reportList, setReportList] = useState<any[]>([])
  const [score, setScore] = useState<any>()
  const [performanceReport, setPerformanceReport] = useState<any>()
  const [totalQuestionInDepart, setTotalQuestionInDepart] = useState<any>()
  // Per-individual points earned by submitting assigned reports (feature #2).
  const [submissionScore, setSubmissionScore] = useState<any>({})
  // Which score to display — "team" (Team Performance) or "dept" (Departments
  // Compliance). Only the selected one is shown on screen and printed, matching
  // the earlier behaviour of choosing one score at a time.
  const [view, setView] = useState<"team" | "dept">("team")
  // Report Submissions data (admins only) — who submitted / missed each report.
  const [submissions, setSubmissions] = useState<any>({ reports: [], rows: [] })
  // Report Submission Calendar (admins only) — per-person daily Y/N/WO grid.
  const [calMonth, setCalMonth] = useState<string>(moment().format("YYYY-MM"))
  const [calendar, setCalendar] = useState<any>({ days: 0, reportKeys: [], rows: [] })

  const fromDate = range[0]
  const toDate = range[1]
  const year = moment(toDate, "YYYY-MM-DD").format("YYYY")

  // Short month names the range spans, for the monthly-based team score.
  const rangeMonths: string[] = []
  {
    let m = moment(fromDate, "YYYY-MM-DD").startOf("month")
    const end = moment(toDate, "YYYY-MM-DD").endOf("month")
    while (m.isSameOrBefore(end)) {
      rangeMonths.push(m.format("MMM"))
      m = m.add(1, "month")
    }
  }

  useEffect(() => {
    if (!selectedFactory) return
    const { orgId } = selectedFactory
    backend.getUserOrg(orgId).then(setUserList).catch(() => {})
    setTotalQuestionInDepart(
      func.getTotalQuestionsindailyMonitoringReport(
        selectedFactory.dailyMonitoringForm,
        selectedFactory.departments
      )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFactory && selectedFactory._id])

  useEffect(() => {
    if (!selectedFactory) return
    const { orgId, _id: facId, teams } = selectedFactory
    backend
      .getdailymonitoringMonthReport(orgId, facId, fromDate, toDate)
      .then((r: any) => setReportList(r || []))
      .catch(() => {})
    backend
      .getReportSubmissionScore(orgId, facId, fromDate, toDate)
      .then((r: any) => setSubmissionScore(r || {}))
      .catch(() => {})
    if (props.isAdmin) {
      backend
        .getReportSubmissions(orgId, facId, fromDate, toDate)
        .then((r: any) => setSubmissions(r || { reports: [], rows: [] }))
        .catch(() => {})
    }
    backend
      .getmonthlyMonitoringReport(orgId, facId, year)
      .then((res: any) => {
        if (res && res.report) {
          setScore(
            func.monthlyMonitoringScoreCalc(
              selectedFactory.monthlyMonitoringForm,
              selectedFactory.responsibility,
              selectedFactory.teams,
              res.report,
              year
            )
          )
        }
      })
      .catch(() => {})
    backend
      .getmonthlyperformaceReport(orgId, facId, `${year}-01-01`, `${year}-12-31`)
      .then((res: any) => {
        if (res) setPerformanceReport(func.performanceReportMonthly(res, teams, year))
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFactory && selectedFactory._id, fromDate, toDate, year])

  // Report Submission Calendar (admins) — reloads when the month changes.
  useEffect(() => {
    if (!selectedFactory || !props.isAdmin) return
    const { orgId, _id: facId } = selectedFactory
    backend
      .getReportCalendar(orgId, facId, calMonth)
      .then((r: any) => setCalendar(r || { days: 0, reportKeys: [], rows: [] }))
      .catch(() => setCalendar({ days: 0, reportKeys: [], rows: [] }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFactory && selectedFactory._id, calMonth, props.isAdmin])

  // Team members who have at least one report assigned (Yes) in Report Scoring.
  // People with no reports assigned (all "No") are excluded from the scores.
  const assignedUserIds = new Set(
    ((selectedFactory && selectedFactory.reportScoring) || [])
      .filter((e: any) => (e.items || []).some((it: any) => Number(it.score) > 0))
      .map((e: any) => String(e.userId))
  )

  // ---- Team performance: per-member average across the months in range ----
  const teamRows = () => {
    if (!selectedFactory || !selectedFactory.teams) return { rows: [], avg: 0 }
    const rows = selectedFactory.teams
      .filter((team: any) => team.status !== false) // hide inactive staff
      .filter((team: any) => assignedUserIds.has(String(team.userId))) // must have a report assigned
      .filter((team: any) => team.userId === props.user["_id"] || props.isAdmin)
      .map((team: any) => {
        const find: any = _.find(userList, ["_id", team.userId])
        // Score out of 100: the person's average completion across their assigned
        // reports (submitted ÷ expected), computed on the backend.
        const value = Number(submissionScore && submissionScore[team.userId]) || 0
        return { name: find ? `${find.name} (${find.role})` : "—", value, present: !!find }
      })
      .filter((r: any) => r.present)
    const avg = rows.length ? Math.round(_.sumBy(rows, "value") / rows.length) : 0
    return { rows, avg }
  }

  // ---- Departments compliance over the selected date range ----
  const deptRows = () => {
    if (!selectedFactory) return { rows: [], avg: 0 }
    const inRange = _.filter(reportList, (o: any) =>
      moment(o.date, "YYYY-MM-DD").isBetween(fromDate, toDate, undefined, "[]") &&
      // A rejected report is not counted towards the compliance score.
      !(o.validation && o.validation.status === "rejected")
    )
    const rows = (selectedFactory.departments || []).map((depart: any) => {
      let value = 0
      if (inRange.length > 0) {
        const report = [].concat.apply([], _.map(inRange, "report"))
        // Score from the questions ACTUALLY RECORDED in the reports, not from the
        // current form's question count. This keeps past dates immune to later
        // question edits: deleting (or adding) a question today does not change an
        // already-answered day's score.
        //   divisor  = select answers recorded for this department in the period
        //   numerator = those answered Yes/NA (value "...:1")
        const answered = _.filter(report, { departmentId: depart._id, type: "select" })
          .filter((f: any) => f.value)
        const yes = answered.filter((f: any) => Number(String(f.value).split(":")[1]) === 1)
        const byValue = answered.length
        value = byValue ? Math.round((yes.length / byValue) * 100) : 0
      }
      return { name: depart.name, holder: depart.holderName, value }
    })
    const avg = rows.length ? Math.round(_.sumBy(rows, "value") / rows.length) : 0
    return { rows, avg }
  }

  const [downloading, setDownloading] = useState(false)
  // Build the current view's table and let the backend render it to PDF.
  const downloadPdf = async () => {
    setDownloading(true)
    try {
      const t = teamRows()
      const d = deptRows()
      const title = view === "team" ? "Team Performance Score" : "Departments Compliance Score"
      let columns: string[]
      let rows: any[][]
      const sections: any[] = []
      if (view === "team") {
        columns = ["Name (Designation)", "Average Score"]
        rows = t.rows.map((r: any) => [r.name, { score: r.value }])
        if (t.rows.length) rows.push(["Average Score", { score: t.avg }])
        // Admins: append the Report Submissions breakup (submitted/expected per report).
        const activeSubRows = (submissions.rows || []).filter((r: any) => r.active !== false)
        if (props.isAdmin && activeSubRows.length) {
          const keys = orderReports(submissions.reports || [])
          const subCell = (r: any) => (!r || !r.assigned) ? "—" : `${r.submitted}/${r.expected}`
          sections.push({
            title: "Report Submissions (submitted / expected)",
            columns: ["Individual", ...keys.map((k) => SUB_LABELS[k] || k)],
            rows: activeSubRows.map((row: any) => [
              `${row.name}${row.role ? ` (${row.role})` : ""}`,
              ...keys.map((k) => subCell(row.reports[k])),
            ]),
          })
        }
        // Admins: append the Submission Calendar (daily Y/N/WO/H/A grid).
        const activeCalRows = (calendar.rows || []).filter((r: any) => r.active !== false)
        if (props.isAdmin && activeCalRows.length && calendar.days) {
          const dayCols = Array.from({ length: calendar.days }, (_, i) => String(i + 1))
          const calRows: any[][] = []
          activeCalRows.forEach((row: any) => {
            // Shaded full-width staff header row (clearly segregates each staff),
            // then one row per assigned daily report.
            calRows.push([{ group: `${row.name}${row.role ? ` (${row.role})` : ""}` }])
            ;(calendar.reportKeys || []).filter((k: string) => row.reports[k]).forEach((k: string) => {
              const rr = row.reports[k]
              calRows.push([rr.label, ...dayCols.map((d) => rr.cells[Number(d)] || ""), String(rr.submitted)])
            })
          })
          sections.push({
            title: `Submission Calendar - ${moment(calMonth, "YYYY-MM").format("MMMM YYYY")}`,
            columns: ["Individual / Report", ...dayCols, "Done"],
            rows: calRows,
          })
        }
      } else {
        columns = ["Departments", "Name", "Average Score"]
        rows = d.rows.map((r: any) => [r.name, r.holder, { score: r.value }])
        if (d.rows.length) rows.push(["", "Average Score", { score: d.avg }])
      }
      await backend.downloadTablePdf(
        selectedFactory.orgId,
        selectedFactory._id,
        { title, subtitle: `${fromDate} to ${toDate}`, columns, rows, landscape: sections.length > 0, sections },
        `${title} - ${String(fromDate).split("-").reverse().join("")}.pdf`
      )
    } catch (e) {
      Toaster.error(typeof e === "string" ? e : "Download failed")
    } finally {
      setDownloading(false)
    }
  }

  if (!selectedFactory) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="mw-page-header">
            <div className="mw-page-title">
              <h4>Performance Score</h4>
              <p>Select a factory to view its performance.</p>
            </div>
          </div>
        </Container>
      </div>
    )
  }

  const team = teamRows()
  const dept = deptRows()

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="mw-page-header">
            <div className="mw-page-title">
              <h4>Performance Score</h4>
              <p>{selectedFactory.name} — Team &amp; Department Scoring for {fromDate} to {toDate}</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ width: 240 }}>
                <Flatpickr
                  className="form-control d-block"
                  value={range}
                  options={{ mode: "range", dateFormat: "Y-m-d" }}
                  onChange={(dates: any) => {
                    if (dates.length === 2) {
                      setRange([
                        moment(dates[0]).format("YYYY-MM-DD"),
                        moment(dates[1]).format("YYYY-MM-DD"),
                      ])
                    }
                  }}
                />
              </div>
              <Button color="success" onClick={downloadPdf} disabled={downloading}>
                {downloading ? "Preparing…" : "Download PDF"}
              </Button>
            </div>
          </div>

          {/* View toggle at the top: Team / Departments / (admin) Report Submissions. */}
          <Row className="mb-3">
            <Col lg={12} className="d-flex justify-content-center">
              <ButtonGroup>
                <Button
                  color={view === "team" ? "primary" : "light"}
                  onClick={() => setView("team")}
                >
                  Team Performance Score
                </Button>
                <Button
                  color={view === "dept" ? "primary" : "light"}
                  onClick={() => setView("dept")}
                >
                  Departments Compliance Score
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          {/* Detailed score table: compact, centered. */}
          <div>
            {/* Team Performance Score */}
            {view === "team" && (
            <Row className="justify-content-center">
              <Col lg={7} md={10}>
                <Card>
                  <CardBody>
                    <div style={{ textAlign: "center", marginBottom: "12px" }}>
                      <h5 style={{ color: "#1D3A5F", margin: 0 }}>Team Performance Score</h5>
                      <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                        {selectedFactory.name} &nbsp;|&nbsp; {fromDate} to {toDate}
                      </p>
                    </div>
                    <div className="table-responsive">
                      <Table className="table mw-report-table mb-0" style={{ textAlign: "center" }}>
                        <thead>
                          <tr className="table-success">
                            <th style={{ textAlign: "left" }}>Name (Designation)</th>
                            <th>Average Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {team.rows.length === 0 && (
                            <tr>
                              <td colSpan={2} style={{ textAlign: "center", color: "#9aa6b2", padding: "28px 0" }}>
                                No team members are configured for this factory yet.
                              </td>
                            </tr>
                          )}
                          {team.rows.map((r: any, i: number) => (
                            <tr key={i}>
                              <th scope="row" style={{ textAlign: "left" }}>{r.name}</th>
                              <td><ScoreBadge value={r.value} /></td>
                            </tr>
                          ))}
                          {team.rows.length > 0 && (
                            <tr className="mw-avg-row">
                              <th scope="row" style={{ textAlign: "left" }}>Average Score</th>
                              <td><ScoreBadge value={team.avg} /></td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            )}

            {/* Departments Compliance Score */}
            {view === "dept" && (
            <Row className="justify-content-center">
              <Col lg={8} md={11}>
                <Card>
                  <CardBody>
                    <div style={{ textAlign: "center", marginBottom: "12px" }}>
                      <h5 style={{ color: "#1D3A5F", margin: 0 }}>Departments Compliance Score</h5>
                      <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                        {selectedFactory.name} &nbsp;|&nbsp; {fromDate} to {toDate}
                      </p>
                    </div>
                    <div className="table-responsive">
                      <Table className="table mw-report-table mb-0" style={{ textAlign: "center" }}>
                        <thead>
                          <tr className="table-success">
                            <th style={{ textAlign: "left" }}>Departments</th>
                            <th style={{ textAlign: "left" }}>Name</th>
                            <th>Average Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dept.rows.length === 0 && (
                            <tr>
                              <td colSpan={3} style={{ textAlign: "center", color: "#9aa6b2", padding: "28px 0" }}>
                                No department scores for the selected date range.
                              </td>
                            </tr>
                          )}
                          {dept.rows.map((r: any, i: number) => (
                            <tr key={i}>
                              <th scope="row" style={{ textAlign: "left" }}>{r.name}</th>
                              <th scope="row" style={{ textAlign: "left" }}>{r.holder}</th>
                              <td><ScoreBadge value={r.value} /></td>
                            </tr>
                          ))}
                          {dept.rows.length > 0 && (
                            <tr className="mw-avg-row">
                              <th></th>
                              <th scope="row" style={{ textAlign: "left" }}>Average Score</th>
                              <td><ScoreBadge value={dept.avg} /></td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            )}

            {/* Report Submissions (admins only) — shown under the Team table */}
            {view === "team" && props.isAdmin && (
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <div style={{ textAlign: "center", marginBottom: "6px" }}>
                      <h5 style={{ color: "#1D3A5F", margin: 0 }}>Report Submissions</h5>
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7785", marginBottom: 10 }}>
                      Each cell shows <strong>submitted ÷ expected</strong> for the period ·&nbsp;
                      <Badge color="success" pill> Submitted</Badge>&nbsp;
                      <Badge color="warning" pill> Partial</Badge>&nbsp;
                      <Badge color="danger" pill> Not submitted</Badge>&nbsp;
                      · <strong>—</strong> = report not assigned (set in Report Scoring)
                    </div>
                    <div className="table-responsive">
                      <Table className="table table-bordered mb-0" style={{ textAlign: "center", verticalAlign: "middle" }}>
                        <thead>
                          <tr className="table-success">
                            <th style={{ textAlign: "left" }}>Individual</th>
                            {orderReports(submissions.reports || []).map((key: string) => <th key={key}>{SUB_LABELS[key] || key}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {(submissions.rows || []).filter((r: any) => r.active !== false).length === 0 && (
                            <tr><td colSpan={(submissions.reports || []).length + 1} className="text-muted py-4">No active team members configured for this factory.</td></tr>
                          )}
                          {(submissions.rows || []).filter((r: any) => r.active !== false).map((row: any) => (
                            <tr key={row.userId}>
                              <th scope="row" style={{ textAlign: "left" }}>
                                {row.name} {row.role ? <small className="text-muted">({row.role})</small> : null}
                              </th>
                              {orderReports(submissions.reports || []).map((key: string) => <td key={key}><SubCell r={row.reports[key]} /></td>)}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            )}

            {props.isAdmin && view === "team" && (
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                      <h4 className="card-title mb-0" style={{ textAlign: "center", flex: 1 }}>Submission Calendar</h4>
                      <input type="month" className="form-control" style={{ maxWidth: 170 }}
                        value={calMonth} onChange={(e) => setCalMonth(e.target.value || calMonth)} />
                    </div>
                    <div className="text-muted text-center" style={{ fontSize: 13, marginBottom: 10 }}>
                      Daily report submissions for {moment(calMonth, "YYYY-MM").format("MMMM YYYY")} ·{" "}
                      <Badge color="success" pill>Y</Badge> submitted&nbsp;
                      <Badge color="danger" pill>N</Badge> not submitted&nbsp;
                      <Badge color="secondary" pill>WO</Badge> weekly off&nbsp;
                      <Badge color="warning" pill>H</Badge> holiday&nbsp;
                      <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: 10, background: "#efeaf9", color: "#5b4b9b", border: "1px solid #d9cff2", fontWeight: 700, fontSize: 12, lineHeight: 1.1 }}>A</span> audit
                    </div>
                    <div className="table-responsive">
                      <Table className="table table-bordered mb-0" style={{ textAlign: "center", verticalAlign: "middle", fontSize: 12 }}>
                        <thead>
                          <tr className="table-success">
                            <th style={{ textAlign: "left", minWidth: 190 }}>Individual / Report</th>
                            {Array.from({ length: calendar.days || 0 }, (_, i) => i + 1).map((d: number) => (
                              <th key={d} style={{ padding: "4px 2px" }}>{d}</th>
                            ))}
                            <th style={{ padding: "4px 6px" }}>Done</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(calendar.rows || []).filter((r: any) => r.active !== false).length === 0 && (
                            <tr><td colSpan={(calendar.days || 0) + 2} className="text-muted py-4">No daily reports assigned for this month.</td></tr>
                          )}
                          {(calendar.rows || []).filter((r: any) => r.active !== false).map((row: any) => {
                            const keys = (calendar.reportKeys || []).filter((k: string) => row.reports[k])
                            return (
                              <React.Fragment key={row.userId}>
                                <tr className="table-light">
                                  <th scope="row" colSpan={(calendar.days || 0) + 2} style={{ textAlign: "left" }}>
                                    {row.name} {row.role ? <small className="text-muted">({row.role})</small> : null}
                                  </th>
                                </tr>
                                {keys.map((k: string) => {
                                  const rr = row.reports[k]
                                  return (
                                    <tr key={k}>
                                      <td style={{ textAlign: "left", paddingLeft: 18 }}>{rr.label}</td>
                                      {Array.from({ length: calendar.days || 0 }, (_, i) => i + 1).map((d: number) => (
                                        <td key={d} style={{ padding: "2px 1px" }}><CalCell v={rr.cells[d] || ""} /></td>
                                      ))}
                                      <td style={{ fontWeight: 700 }}>{rr.submitted}</td>
                                    </tr>
                                  )
                                })}
                              </React.Fragment>
                            )
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            )}
          </div>
        </Container>
      </div>
    </React.Fragment>
  )
}

const mapStateToProps = (state: any) => {
  const { user } = state.user
  const { selected: selectedFactory } = state.factory
  const isAdmin =
    selectedFactory && selectedFactory.permission.includes("ADMIN") ? true : false
  return { selectedFactory, user, isAdmin }
}

export default withRouter(connect(mapStateToProps, {})(PerformaceScore))
